import { ApexOptions } from 'apexcharts'
import BaseChartCard from 'common/components/BaseChartCard'
import DateRangePicker, { DateRange } from 'common/components/DateRangePicker'
import BaseOptionChart from 'common/components/charts/BaseOptionChart'
import { CostPerUnit } from 'common/constants/pond'
import { dynamicTickAmount, integerFormatter } from 'common/utils/chart'
import { formatDate, getTodayDate } from 'common/utils/date'
import { fNumber } from 'common/utils/formatNumber'
import merge from 'lodash/merge'
import { useGetTransactionLineChart } from 'modules/cost/api/queries'
import { dashboardDateDisplayValue } from 'modules/food/utils'
import { useCurrentCycleInfo, useSpeciesName } from 'modules/pond/api/selector'
import { PondStatus } from 'modules/pond/constants'
import { useIsCycleBeforeThreshold } from 'modules/pond/stores/selector'
import { useEffect, useRef, useState } from 'react'
import ReactApexChart from 'react-apexcharts'
import { useTranslation } from 'react-i18next'

import AutorenewIcon from '@mui/icons-material/Autorenew'
import { Box, Skeleton, Stack, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'

import ChartFallback from 'components/ChartFallback'

const getDefaultDateRange = (anchorEndDate?: Date | null): DateRange => {
  const end = anchorEndDate ? new Date(anchorEndDate) : getTodayDate()
  const start = new Date(end)
  start.setDate(end.getDate() - 6)
  return { startDate: start, endDate: end }
}

const CostIncomeLineChart = () => {
  const { t } = useTranslation('cost')
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'))
  const species = useSpeciesName()

  const isCycleBeforeThreshold = useIsCycleBeforeThreshold()
  const cycleInfo = useCurrentCycleInfo()
  const cycleAnchorEndDate =
    cycleInfo?.status === PondStatus.Completed && cycleInfo.endDate
      ? new Date(cycleInfo.endDate)
      : null
  const cycleStartDate = cycleInfo?.startDate ? new Date(cycleInfo.startDate) : undefined
  const cycleMaxDate = cycleAnchorEndDate ?? getTodayDate()

  const [costPerUnit, setCostPerUnit] = useState<CostPerUnit>(CostPerUnit.KG)
  const [dateRange, setDateRange] = useState<DateRange>(() =>
    getDefaultDateRange(cycleAnchorEndDate)
  )

  const lastCycleIdRef = useRef<string | null>(cycleInfo?.id ?? null)
  useEffect(() => {
    if (!cycleInfo?.id) return
    if (lastCycleIdRef.current === cycleInfo.id) return
    lastCycleIdRef.current = cycleInfo.id
    setDateRange(getDefaultDateRange(cycleAnchorEndDate))
  }, [cycleInfo?.id, cycleAnchorEndDate])

  const formatDateForApi = (date: Date) => formatDate(date)

  const { data, isFetching, refetch } = useGetTransactionLineChart({
    startDate: dateRange.startDate ? formatDateForApi(dateRange.startDate) : undefined,
    endDate: dateRange.endDate ? formatDateForApi(dateRange.endDate) : undefined,
    costPerUnit,
  })

  const series = [
    {
      name: t('cost.title', 'Cost'),
      data: data ? data.map((d) => [d.age, d.value ?? 0]) : [],
    },
  ]

  const chartOptions: ApexOptions = merge(BaseOptionChart(), {
    chart: {
      type: 'line',
      toolbar: { show: false },
    },
    xaxis: {
      type: 'numeric',
      tickAmount: dynamicTickAmount(data?.length || 0),
      labels: {
        formatter: integerFormatter,
      },
      title: {
        text: t('chart.age'),
      },
    },
    yaxis: {
      labels: {
        formatter: (value: number) => (isNaN(value) ? '-' : ` ${fNumber(value)}`),
      },
      title: {
        text: costPerUnit === CostPerUnit.COUNT ? t('cost.per.unit.unit') : t('cost.per.kg.unit'),
      },
    },
    tooltip: {
      y: {
        formatter: (value: number) => (isNaN(value) ? '-' : `฿ ${fNumber(value)}`),
      },
      x: {
        show: true,
        formatter: (v: number) => {
          return `${t('age', { speciesName: species })}: ${v} ${t('unit.day')}`
        },
      },
    },
    stroke: {
      curve: 'straight',
      width: 2,
    },
    markers: {
      size: 4,
    },
    colors: [theme.palette.primary.main],
  })

  return (
    <BaseChartCard>
      <Stack
        direction={isMobile ? 'column' : 'row'}
        justifyContent="space-between"
        alignItems={isMobile ? 'flex-start' : 'center'}
        gap={1}
        mb={2}
      >
        <Stack direction={isMobile ? 'column' : 'row'} gap={0.5}>
          <Typography fontWeight={600}>
            {costPerUnit === CostPerUnit.COUNT
              ? t('chart.cost.title.per.unit')
              : t('chart.cost.title.per.kg')}
          </Typography>

          <Box
            onClick={() =>
              setCostPerUnit(costPerUnit === CostPerUnit.COUNT ? CostPerUnit.KG : CostPerUnit.COUNT)
            }
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              cursor: 'pointer',
              color: 'primary.main',
            }}
          >
            <AutorenewIcon fontSize="small" />
            <Typography variant="body2" fontWeight={500}>
              {costPerUnit === CostPerUnit.COUNT ? t('cost.per.kg') : t('cost.per.unit')}
            </Typography>
          </Box>
        </Stack>

        <Box className="dashboard-chart-datepicker">
          <DateRangePicker
            value={dateRange}
            onChange={setDateRange}
            minDate={cycleStartDate}
            maxDate={cycleMaxDate}
            showLabel={false}
            displayValue={dashboardDateDisplayValue}
            handleClear={() => setDateRange(getDefaultDateRange(cycleAnchorEndDate))}
          />
        </Box>
      </Stack>

      {isFetching ? (
        <Skeleton variant="rectangular" height={300} />
      ) : isCycleBeforeThreshold ? (
        <ChartFallback height={300} />
      ) : !data || data.length === 0 ? (
        <ReactApexChart
          type="line"
          series={[{ name: t('chart.no.data'), data: [[0, 0]] }]}
          options={chartOptions}
          height={300}
        />
      ) : (
        <ReactApexChart type="line" series={series} options={chartOptions} height={300} />
      )}
    </BaseChartCard>
  )
}

export default CostIncomeLineChart
